import { isAddedSugars, getDietaryLabel } from "./nutrients.js";

export function formatRawAmount(line) {
  if (!line.amount) return "";
  const unit = line.unit || "mg";
  return `${line.amount} ${unit}`;
}

export function formatBlendAmount(blend) {
  if (!blend.amount) return "";
  return blend.unit ? `${blend.amount} ${blend.unit}` : blend.amount;
}

export function formatConstituents(constituents) {
  if (!constituents || !Array.isArray(constituents)) return "";
  return constituents
    .map((c) => {
      let name = formatLabelName(c.name, {
        italicizeParenthetical: c.italicize_parenthetical,
        disableSubscripts: true,
      });
      if (c.source) {
        name += ` (${renderMarkdown(c.source)})`;
      }
      if (c.footnoteHtml) {
        name += `<span class="custom-footnote-symbol icon-text">${c.footnoteHtml}</span>`;
      }
      if (c.extra_text) {
        return `<span style="display: inline-block; vertical-align: top">${name}<div class="SupplementFacts-ExtraText">${renderMarkdown(c.extra_text)}</div></span>`;
      }
      return name;
    })
    .filter(Boolean)
    .join(", ");
}

export function formatConstituentAmount(constituent) {
  if (!constituent.amount) return "";
  return constituent.unit ? `${constituent.amount} ${constituent.unit}` : constituent.amount;
}

export function formatDietaryAmount(line) {
  // Just format the raw amount for display - backend handles rounding
  return formatRawAmount(line);
}

// Get display name for DV lines
export function getDisplayName(line, calculatedAmount) {
  if (isAddedSugars(line)) {
    return calculatedAmount ? `Includes ${calculatedAmount} Added Sugars` : "Added Sugars";
  }
  return line.name;
}

// Get display name for dietary lines (uses backend metadata for fallback)
export function getDietaryDisplayName(line, formattedAmount, store) {
  if (isAddedSugars(line)) {
    return formattedAmount ? `Includes ${formattedAmount} Added Sugars` : "Added Sugars";
  }
  return store ? getDietaryLabel(line.dv_key, store) : line.dv_key;
}

export function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

// Apply markdown formatting: **bold** and _italics_
export function applyMarkdownFormatting(text) {
  return text.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>").replace(/_(.+?)_/g, "<i>$1</i>");
}

export function renderMarkdown(text) {
  if (!text) return "";
  let formatted = escapeHtml(text);
  formatted = applyMarkdownFormatting(formatted);
  return formatted;
}

// Convert digits after letters to subscript (e.g., B12 → B₁₂, D3 → D₃)
function toSubscript(str) {
  // Replace digits that follow a letter with subscript versions
  // Matches: letter followed by one or more digits (e.g., B12, D3, K2)
  return str.replace(/([A-Za-z])(\d+)/g, "$1<sub>$2</sub>");
}

export function formatLabelName(name, italicizeParentheticalOrOptions) {
  if (!name) return "";

  // Handle options argument (supports legacy boolean for backward compatibility)
  let italicizeParenthetical = false;
  let disableSubscripts = false;

  if (typeof italicizeParentheticalOrOptions === "object") {
    italicizeParenthetical = italicizeParentheticalOrOptions.italicizeParenthetical;
    disableSubscripts = italicizeParentheticalOrOptions.disableSubscripts;
  } else {
    italicizeParenthetical = italicizeParentheticalOrOptions;
  }

  // Escape HTML to prevent XSS (we'll re-inject safe HTML tags for formatting)
  let formatted = escapeHtml(name);

  // Apply subscript formatting (unless disabled)
  if (!disableSubscripts) {
    formatted = toSubscript(formatted);
  }

  // Apply markdown formatting: **bold** and _italics_
  formatted = applyMarkdownFormatting(formatted);

  if (italicizeParenthetical) {
    // Italicize content inside parentheses, but avoid breaking existing tags if possible
    // (Simpler regex approach: match parens that don't look like they are inside tags)
    formatted = formatted.replace(/\(([^)]+)\)/g, "(<i>$1</i>)");
  }

  return formatted;
}

export { getNextAvailableFootnoteChar } from "./footnote-symbols.js";

export function preventScientificNotation(event) {
  if (["e", "E", "+", "-"].includes(event.key)) {
    event.preventDefault();
  }
}

export function sanitizeFootnoteChar(value) {
  if (!value) return "";
  const char = String(value).toUpperCase().charAt(0);
  if (/^[1-9A-Z]$/.test(char)) return char;
  return "";
}

export function preventInvalidFootnoteChar(event) {
  const key = event.key.toUpperCase();
  if (key.length === 1 && !/^[1-9A-Z]$/.test(key)) {
    event.preventDefault();
  }
}

// Returns FontAwesome icon class for 1-9 and A-Z footnote symbols (internal helper)
function getFootnoteIconClass(charOrIndex) {
  if (charOrIndex === undefined || charOrIndex === null) return "";

  const str = String(charOrIndex).toLowerCase();

  if (/^[a-z]$/.test(str)) {
    return `fa-solid fa-circle-${str}`;
  }

  const num = parseInt(str, 10);
  if (!isNaN(num) && num >= 1 && num <= 9) {
    return `fa-solid fa-circle-${num}`;
  }

  return "";
}

// Returns HTML for footnote symbol using FontAwesome icon
export function getFootnoteSymbol(charOrIndex) {
  const iconClass = getFootnoteIconClass(charOrIndex);
  if (!iconClass) return "";
  return `<i class="${iconClass}"></i>`;
}

// --- Italicize Parenthetical Helpers ---

export function hasParentheses(name) {
  if (!name) return false;
  return name.includes("(") && name.includes(")");
}

export function isLatinParenthetical(name) {
  if (!name) return false;
  const match = name.match(/\(([^)]+)\)/);
  if (!match) return false;
  const inside = match[1];
  // Check if starts with "as " (case-insensitive) - NOT Latin
  if (/^as\s/i.test(inside)) return false;
  // Check if first character is uppercase - Latin
  return /^[A-Z]/.test(inside);
}

export function getItalicizeLabel(name) {
  return isLatinParenthetical(name) ? "Italicize Latin/parenthetical" : "Italicize parenthetical";
}

// --- Prefix Detection Helpers ---
// For detecting patterns like "Vitamin A: " at the start of text fields

export const BARE_PREFIX_PATTERN = /^[A-Z][A-Za-z\s/]*:\s?/;

export function stripMarkdownMarkersForPrefix(str) {
  return str.replace(/\*+/g, "").replace(/_+/g, "");
}

export function hasRealContent(str) {
  if (!str) return false;
  return str.replace(/[\s_*]/g, "").length > 0;
}

export function hasContentBeyondPrefix(text) {
  if (!text) return false;
  const prefixEnd = findPrefixEndIndex(text);
  if (prefixEnd === -1) return hasRealContent(text);
  const rest = text.slice(prefixEnd);
  return hasRealContent(rest);
}

export function findPrefixEndIndex(text) {
  if (!text) return -1;
  const stripped = stripMarkdownMarkersForPrefix(text);
  const match = stripped.match(BARE_PREFIX_PATTERN);
  if (!match) return -1;

  const targetColonIndex = match[0].indexOf(":");
  let colonCount = 0;
  let realColonIndex = -1;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === "*" || ch === "_") continue;
    if (ch === ":") {
      if (colonCount === targetColonIndex) {
        realColonIndex = i;
        break;
      }
      colonCount++;
    } else {
      colonCount++;
    }
  }

  if (realColonIndex === -1) return -1;

  let endIndex = realColonIndex + 1;
  while (endIndex < text.length && (text[endIndex] === "*" || text[endIndex] === "_")) {
    endIndex++;
  }
  if (endIndex < text.length && text[endIndex] === " ") {
    endIndex++;
  }

  return endIndex;
}
